import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Shield } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="flex flex-col min-h-screen pb-20 pt-10">
      <SEO 
        title="About RC Studios | Gregory Agri"
        description="Meet Gregory Agri, owner of RC Studios. Professional real estate photography serving Kingman, Bullhead City, Lake Havasu, and Mohave County. Fully insured."
        path="/about"
      />
      {/* Header */}
      <section className="container mx-auto px-4 md:px-6 text-center max-w-4xl mb-16">
        <Badge className="mb-4 bg-[#F5C056]/10 text-[#F5C056] hover:bg-[#F5C056]/20 border-none">THE CREATOR</Badge>
        <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 text-foreground">
          Capturing the Art of <span className="text-[#F5C056]">Living</span>
        </h1>
        <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          Photography isn&apos;t just about documenting a space; it&apos;s about translating the feeling of home into a visual language that connects.
        </p>
      </section>

      {/* Image & Story */}
      <section className="container mx-auto px-4 md:px-6 max-w-[1200px]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-24 items-start">
          
          {/* Portrait Image - Gregory Agri */}
          <div className="lg:col-span-5 relative lg:sticky lg:top-32">
            <div className="relative aspect-[3/4] max-w-[400px] mx-auto lg:ml-auto">
              {/* Subtle accent line behind image */}
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#F5C056]/30 -z-10"></div>
              
              {/* Gregory Agri headshot */}
              <img 
                src="/gregory-agri-headshot-optimized.webp" 
                alt="Gregory Agri - Owner & Lead Photographer at RC Studios"
                className="w-full h-full object-cover shadow-2xl"
                data-testid="owner-headshot"
              />
            </div>
            
            {/* Insurance badge */}
            <div className="mt-6 text-center">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200">
                <Shield className="w-4 h-4 text-[#F5C056]" />
                <span className="text-sm text-gray-600">Fully Insured (Progressive)</span>
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div className="lg:col-span-7 space-y-10 lg:pt-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                Gregory Agri
              </h2>
              <p className="text-[#F5C056] text-sm font-semibold tracking-wider uppercase mb-8">
                Owner & Lead Photographer
              </p>
              
              <div className="space-y-8 text-lg text-muted-foreground leading-relaxed font-light">
                <p>
                  I approach every shoot not as a checklist, but as a narrative waiting to be told. Whether it&apos;s the architectural symmetry of a modern estate or the warm glow of a twilight exterior, my goal is to craft images that stop the scroll and start a conversation.
                </p>
                <p>
                  When you work with me, there is no rushing the process. Every project deserves the time and attention it needs to be done right. I&apos;m here to understand your vision, not move you through a preset time slot.
                </p>
                <p>
                  I&apos;ve seen how often photography is treated as an in-and-out service. My approach is different. I work closely with my clients from capture to final delivery, staying fully involved through every stage of the process. All editing is done in-house, by me, with the same care and intention as the shoot itself. The result is work that feels considered, personal, and crafted.
                </p>
                <p>
                  While photography is at the core of my work, my services extend beyond a single discipline. I collaborate with clients on a range of creative and private projects, offering a flexible, personalized approach tailored to their goals.
                </p>
              </div>
            </div>

            {/* Editorial Pull Quote */}
            <div className="pt-8 border-t border-[#F5C056]/20">
              <p className="text-2xl md:text-3xl font-serif italic text-foreground leading-normal">
                &quot;I believe quality is personal. When you hire RC Studios, you don&apos;t get a random contractor &mdash; you get my eye, my experience, and my dedication to your brand.&quot;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise & Creative Range */}
      <section className="bg-[#FAF9F6] py-24 border-t border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-[1200px]">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-foreground">Beyond the Listing</h2>
            <p className="text-lg text-muted-foreground leading-relaxed font-light">
              While real estate is a primary focus, my studio operates as a full-service creative house. I bring the same level of precision and artistry to a wide range of visual disciplines.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Artistic Portraiture",
                desc: "Capturing personality and presence with editorial lighting and direction."
              },
              {
                title: "Event Coverage",
                desc: "Documenting moments with a cinematic eye, from corporate gatherings to private celebrations."
              },
              {
                title: "Pet Photography",
                desc: "Honoring your companions with studio-quality portraits full of character."
              },
              {
                title: "Bespoke Creative Projects",
                desc: "Bringing conceptual ideas to life through custom fine art and visual storytelling."
              },
              {
                title: "Brand & Lifestyle",
                desc: "Elevating businesses with cohesive imagery that communicates value and identity."
              },
              {
                title: "Fine Art Prints",
                desc: "Creating one-of-a-kind visual pieces for homes, offices, and collections."
              }
            ].map((item, i) => (
              <Card key={i} className="border-none shadow-sm hover:shadow-md transition-shadow bg-white">
                <CardContent className="p-8">
                  <div className="w-10 h-10 rounded-full bg-[#F5C056]/10 flex items-center justify-center mb-6 text-[#F5C056]">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm font-light">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy / Values Section */}
      <section className="mt-4 pt-8 border-t border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-24 items-center">
            
            {/* Left: Text Content */}
            <div className="lg:col-span-7 order-2 lg:order-1 text-left">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-10 text-foreground">Why I Create</h2>
              <div className="space-y-8 text-lg text-muted-foreground leading-relaxed font-light">
                <p>
                  I create with intention. Not just how something looks, but how it lives in the world.
                </p>
                <p>
                  My work is informed by years of experience in marketing and business advertising, giving me a clear understanding of how visual content performs across platforms. This perspective allows me to craft imagery that supports both creative vision and real business goals without compromising quality or authenticity.
                </p>
                <p>
                  For my clients, that means a true partnership: thoughtful creation, strategic insight, and work designed to make an impact.
                </p>
              </div>
            </div>

            {/* Right: Secondary Visual */}
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative aspect-square max-w-[400px] mx-auto overflow-hidden rounded-full shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/dzf03sml_openart-image_9L6-EDwO_1768420592828_raw%20%281%29.jpg" 
                  alt="Creative Vision" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-[#F5C056]/10 mix-blend-multiply pointer-events-none"></div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
